/**
 * Data Classification Tool Types
 * Classify data sensitivity for compliance
 */

export type DataClassificationLevel =
  | 'public'
  | 'internal'
  | 'confidential'
  | 'restricted';

export type DataCategory =
  | 'personal_data'
  | 'financial_data'
  | 'health_data'
  | 'business_data'
  | 'technical_data'
  | 'legal_data'
  | 'other';

export type RegulationApplicable =
  | 'gdpr'
  | 'hipaa'
  | 'pci_dss'
  | 'ccpa'
  | 'sox'
  | 'ferpa'
  | 'glba'
  | 'none';

export interface DataAsset {
  id: string;
  name: string;
  description?: string;
  category: DataCategory;
  classification: DataClassificationLevel;
  dataTypes: string[];
  location: string;
  owner?: string;
  regulations: RegulationApplicable[];
  retentionPeriod?: string;
  accessControls?: string;
  encryptionStatus: 'encrypted' | 'not_encrypted' | 'partial' | 'unknown';
  backupStatus: 'backed_up' | 'not_backed_up' | 'unknown';
  notes?: string;
  lastReviewDate?: string;
  nextReviewDate?: string;
}

export interface DataClassificationInventory {
  id: string;
  name: string;
  description?: string;
  organization?: string;
  assets: DataAsset[];
  settings: InventorySettings;
  created_at: string;
  updated_at: string;
}

export interface InventorySettings {
  defaultRetention?: string;
  reviewFrequency?: 'monthly' | 'quarterly' | 'annually';
  organizationName?: string;
}

// Configuration
export const CLASSIFICATION_CONFIG: Record<
  DataClassificationLevel,
  {
    label: string;
    description: string;
    color: string;
    examples: string[];
    handlingRequirements: string[];
  }
> = {
  public: {
    label: 'Public',
    description: 'Information intended for public release with no restrictions.',
    color: '#22c55e', // green
    examples: [
      'Marketing materials',
      'Public website content',
      'Press releases',
      'Published research',
    ],
    handlingRequirements: [
      'No special handling required',
      'May be freely shared',
    ],
  },
  internal: {
    label: 'Internal',
    description: 'Information for internal use only, not for public distribution.',
    color: '#3b82f6', // blue
    examples: [
      'Internal policies',
      'Meeting notes',
      'Non-sensitive business data',
      'Internal communications',
    ],
    handlingRequirements: [
      'Share only within organization',
      'Use standard access controls',
      'No external sharing without approval',
    ],
  },
  confidential: {
    label: 'Confidential',
    description: 'Sensitive information requiring protection from unauthorized access.',
    color: '#f59e0b', // amber
    examples: [
      'Customer data',
      'Financial records',
      'Employee PII',
      'Strategic plans',
      'Contracts',
    ],
    handlingRequirements: [
      'Encrypt at rest and in transit',
      'Restrict access on need-to-know basis',
      'Audit access regularly',
      'Secure disposal required',
    ],
  },
  restricted: {
    label: 'Restricted',
    description: 'Highly sensitive data with strict access controls and regulatory requirements.',
    color: '#ef4444', // red
    examples: [
      'Payment card data (PCI)',
      'Health records (PHI)',
      'Trade secrets',
      'Legal holds',
      'Credentials/secrets',
    ],
    handlingRequirements: [
      'Strong encryption required',
      'Multi-factor authentication for access',
      'Detailed audit logging',
      'Regular compliance audits',
      'Incident response plan required',
    ],
  },
};

export const CATEGORY_CONFIG: Record<
  DataCategory,
  {
    label: string;
    description: string;
    icon: string;
    commonRegulations: RegulationApplicable[];
  }
> = {
  personal_data: {
    label: 'Personal Data',
    description: 'Information that can identify an individual',
    icon: '👤',
    commonRegulations: ['gdpr', 'ccpa'],
  },
  financial_data: {
    label: 'Financial Data',
    description: 'Financial records, transactions, payment data',
    icon: '💰',
    commonRegulations: ['pci_dss', 'sox', 'glba'],
  },
  health_data: {
    label: 'Health Data',
    description: 'Protected health information (PHI)',
    icon: '🏥',
    commonRegulations: ['hipaa'],
  },
  business_data: {
    label: 'Business Data',
    description: 'Business operations, strategies, analytics',
    icon: '📊',
    commonRegulations: ['none'],
  },
  technical_data: {
    label: 'Technical Data',
    description: 'System configs, code, infrastructure details',
    icon: '⚙️',
    commonRegulations: ['none'],
  },
  legal_data: {
    label: 'Legal Data',
    description: 'Contracts, litigation, compliance documents',
    icon: '⚖️',
    commonRegulations: ['none'],
  },
  other: {
    label: 'Other',
    description: 'Other types of data',
    icon: '📁',
    commonRegulations: ['none'],
  },
};

export const REGULATION_CONFIG: Record<
  RegulationApplicable,
  {
    label: string;
    fullName: string;
    description: string;
  }
> = {
  gdpr: {
    label: 'GDPR',
    fullName: 'General Data Protection Regulation',
    description: 'EU regulation for personal data protection',
  },
  hipaa: {
    label: 'HIPAA',
    fullName: 'Health Insurance Portability and Accountability Act',
    description: 'US regulation for health information protection',
  },
  pci_dss: {
    label: 'PCI DSS',
    fullName: 'Payment Card Industry Data Security Standard',
    description: 'Standard for handling payment card data',
  },
  ccpa: {
    label: 'CCPA',
    fullName: 'California Consumer Privacy Act',
    description: 'California regulation for consumer data privacy',
  },
  sox: {
    label: 'SOX',
    fullName: 'Sarbanes-Oxley Act',
    description: 'US regulation for financial reporting',
  },
  ferpa: {
    label: 'FERPA',
    fullName: 'Family Educational Rights and Privacy Act',
    description: 'US regulation for student education records',
  },
  glba: {
    label: 'GLBA',
    fullName: 'Gramm-Leach-Bliley Act',
    description: 'US regulation for financial institution data',
  },
  none: {
    label: 'None',
    fullName: 'No specific regulation',
    description: 'No specific regulatory requirements identified',
  },
};

export function generateAssetId(): string {
  return `asset-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

export function generateInventoryId(): string {
  return `inv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}
